/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosRequestConfig } from 'axios';
import { SecureApiService, ISecureApiService } from '../../../api-service';
import { IConfiguration, configuration } from '../../../configuration';
import { IQueryDefinition, IDefaultStartPageSystemQuery } from '../models/dtos';
import { noCacheOptions } from '../helpers';
import { IUsageContext, toUsageQuery } from '../usage/usage-context';

export interface ISavedQueryApiService {
  getAll(): Promise<IQueryDefinition[]>;
  get(savedQueryId: string, config?: AxiosRequestConfig): Promise<IQueryDefinition>;
  getOmitAccessControl(savedQueryId: string, config?: AxiosRequestConfig, usage?: IUsageContext): Promise<IQueryDefinition>;
  delete(savedQueryId: string, config?: AxiosRequestConfig): Promise<any>;
  create(queryDefinition: IQueryDefinition, config?: AxiosRequestConfig): Promise<IQueryDefinition>;
  update(queryDefinition: IQueryDefinition, config?: AxiosRequestConfig): Promise<IQueryDefinition>;
  getSavedQueries(universe: string, resultType: string | undefined, config?: AxiosRequestConfig): Promise<IQueryDefinition[]>;
  getDefaultSystemQuery(universe: string, resultType: string, applicationAreaName: string, config?: AxiosRequestConfig): Promise<IDefaultStartPageSystemQuery>;
  getSavedQueriesByApplicationAreaId(applicationAreaId: string, config?: AxiosRequestConfig): Promise<IQueryDefinition[]>;
}

class SavedQueryApiService implements ISavedQueryApiService {
  private readonly baseService: ISecureApiService = new SecureApiService();
  private readonly configuration: IConfiguration = configuration;

  getAll = async (): Promise<IQueryDefinition[]> => {
    try {
      const url = `${this.getApiAddress()}/SavedQuery`;
      return this.baseService.get(url, undefined, noCacheOptions);
    } catch (error: any) {
      throw new Error(error);
    }
  };

  get = async (savedQueryId: string, config?: AxiosRequestConfig): Promise<IQueryDefinition> => {
    try {
      const url = `${this.getApiAddress()}/SavedQuery/${savedQueryId}`;
      return this.baseService.get(url, undefined, config);
    } catch (error: any) {
      throw new Error(error);
    }
  };
  getOmitAccessControl = async (savedQueryId: string, config?: AxiosRequestConfig, usage?: IUsageContext): Promise<IQueryDefinition> => {
    try {
      const url = `${this.getApiAddress()}/SavedQuery/GetOmitAccessControl/${savedQueryId}`;
      return this.baseService.get(url, toUsageQuery(usage), config);
    } catch (error: any) {
      throw new Error(error);
    }
  };
  create = async (queryDefinition: IQueryDefinition, config?: AxiosRequestConfig): Promise<IQueryDefinition> => {
    try {
      const url = `${this.getApiAddress()}/SavedQuery`;

      return this.baseService.post<IQueryDefinition, any>(url, queryDefinition, config);
    } catch (error: any) {
      throw new Error(error);
    }
  };

  update = async (queryDefinition: IQueryDefinition, config?: AxiosRequestConfig): Promise<IQueryDefinition> => {
    try {
      const url = `${this.getApiAddress()}/SavedQuery/${queryDefinition.id}`;
      return this.baseService.put<IQueryDefinition, any>(url, queryDefinition, config);
    } catch (error: any) {
      throw new Error(error);
    }
  };

  delete = async (savedQueryId: string, config?: AxiosRequestConfig): Promise<any> => {
    try {
      const url = `${this.getApiAddress()}/SavedQuery/${savedQueryId}`;
      return this.baseService.delete<any>(url, config);
    } catch (error: any) {
      throw new Error(error);
    }
  };

  getSavedQueries = async (universe: string, resultType: string | undefined, config?: AxiosRequestConfig): Promise<IQueryDefinition[]> => {
    try {
      const url = `${this.getApiAddress()}/SavedQuery/GetSavedQueries/${universe}`;
      const query = { resultType };
      return this.baseService.get(url, query, config);
    } catch (error: any) {
      throw new Error(error);
    }
  };

  getDefaultSystemQuery = async (universe: string, resultType: string, applicationAreaName: string, config?: AxiosRequestConfig): Promise<IDefaultStartPageSystemQuery> => {
    try {
      const url = `${this.getApiAddress()}/SavedQuery/GetDefaultStartPageSystemSavedQuery/${universe}/${resultType}/${applicationAreaName}`;
      return this.baseService.get(url, undefined, config);
    } catch (error: any) {
      throw new Error(error);
    }
  };

  getSavedQueriesByApplicationAreaId = async (applicationAreaId: string, config?: AxiosRequestConfig): Promise<IQueryDefinition[]> => {
    try {
      const url = `${this.getApiAddress()}/SavedQuery/GetSavedQueriesByApplicationAreaId/${applicationAreaId}`;
      return this.baseService.get(url, undefined, config);
    } catch (error: any) {
      throw new Error(error);
    }
  };

  private readonly getApiAddress = () => this.configuration.searchBaseAddress;
}
export const savedQueryApiService = new SavedQueryApiService();
